// Full Authorize.Net transaction detail dialog for the ReconWorkspace AuthNet config. Stored summary
// shows instantly; deep fields load on demand (live read of /authnet-reconcile/txn). This is the
// row's detail view — the heavy, PSP-specific fields live here, never in the lean table.
import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, Chip, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { get } from '../../../utils/api';
import { fmtMoney, fmtDate, fmtDateTime, ACCOUNT_LABEL, accountColor } from '../reconShared';

const isEcheck = (m) => String(m || '').toLowerCase().includes('echeck');
const methodLabel = (m) => (isEcheck(m) ? 'eCheck (ACH)' : (m || 'card'));
const MATCH_LABEL = {
  trans_id: 'AuthNet ID on order', manual_link: 'AuthNet ID (linked)', invoice: 'Invoice # = order #',
  name_amount: 'Donor name + amount', amount_date: 'Amount + date',
};
const matchLabelOf = (r) => MATCH_LABEL[r.match_method] || (r.status === 'unbooked' ? 'No order found' : '—');

export default function AuthnetTxnDetailDialog({ ctx, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!ctx) { setDetail(null); setErr(''); setLoading(false); return undefined; }
    let cancel = false;
    setDetail(null); setErr(''); setLoading(true);
    (async () => {
      try { const d = await get('/api/manage/authnet-reconcile/txn', { account: ctx.account, trans_id: ctx.trans_id }); if (!cancel) setDetail(d); }
      catch (e) { if (!cancel) setErr(e?.response?.data?.error || e?.message || 'Failed to load detail'); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [ctx]);
  if (!ctx) return null;
  const r = ctx;
  const retry = async () => {
    setLoading(true); setErr('');
    try { setDetail(await get('/api/manage/authnet-reconcile/txn', { account: r.account, trans_id: r.trans_id })); }
    catch (e) { setErr(e?.response?.data?.error || e?.message || 'Failed to load detail'); }
    finally { setLoading(false); }
  };
  const statusColor = (s) => { const v = String(s || '').toLowerCase(); if (v.includes('return') || v.includes('decline') || v.includes('fail') || v.includes('error')) return 'error'; if (v.includes('settle') || v.includes('approv')) return 'success'; if (v.includes('refund') || v.includes('void') || v.includes('hold')) return 'warning'; return 'default'; };
  const Field = ({ label, value, mono }) => (
    <Box sx={{ display: 'flex', gap: 1.5, py: 0.7, borderBottom: '1px dashed', borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 116, flexShrink: 0, pt: '2px' }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontFamily: mono ? 'monospace' : 'inherit', fontSize: mono ? 12.5 : 14, wordBreak: 'break-word', textAlign: 'right', flex: 1 }}>{(value === 0 || value) ? value : '—'}</Typography>
    </Box>
  );
  const Section = ({ title, children }) => (
    <Box sx={{ mb: 1.75 }}>
      <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.6 }}>{title}</Typography>
      <Box sx={{ mt: 0.25 }}>{children}</Box>
    </Box>
  );
  const status = detail?.status || r.txn_status;
  const donor = (detail?.bill_to?.name) || r.donor_name;
  const isReturnedStatus = String(detail?.status || r.txn_status || '').toLowerCase().includes('return') || r.status === 'returned';
  const returnText = (Array.isArray(detail?.returned_items) && detail.returned_items.length)
    ? detail.returned_items.map((ri) => `${ri.code || ''} ${ri.description || ''}`.trim()).filter(Boolean).join('; ')
    : (detail?.response_reason || r.returned_reason || '');
  const payLine = detail?.payment
    ? (detail.payment.kind === 'echeck'
        ? `eCheck · ${[detail.payment.bank, detail.payment.account_type, detail.payment.mask].filter(Boolean).join(' ')}${detail.payment.echeck_type ? ` (${detail.payment.echeck_type})` : ''}`
        : detail.payment.kind === 'card' ? `${detail.payment.brand || 'Card'} ${detail.payment.mask || ''}`.trim() : null)
    : `${methodLabel(r.method)}${r.account_mask ? ` · ${r.account_mask}` : ''}`;
  return (
    <Dialog open={!!ctx} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Transaction detail</Typography>
          <Chip size="small" variant="outlined" color={accountColor(r.account)} label={ACCOUNT_LABEL[r.account] || r.account} />
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'grey.50' }}>
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: r.status === 'returned' ? 'error.main' : 'text.primary' }}>{fmtMoney(r.amount_num)}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{donor || 'no donor captured'}</Typography>
              {detail?.email && <Typography variant="caption" sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>{detail.email}</Typography>}
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Chip size="small" color={statusColor(status)} label={status || '—'} />
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.75 }}>{fmtDate(r.created_at)}</Typography>
            </Box>
          </Box>
        </Paper>

        {loading && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, color: 'text.secondary' }}><CircularProgress size={15} /><Typography variant="caption">Loading full Authorize.Net detail…</Typography></Box>}
        {err && <Alert severity="warning" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" onClick={retry}>Retry</Button>}>{err}</Alert>}
        {isReturnedStatus && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            <b>Returned</b> — {returnText || 'the bank reversed this payment'}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Section title="Payment">
            <Field label="Method" value={payLine} />
            <Field label="Auth / settle" value={detail ? `${fmtMoney(detail.auth_amount)} / ${fmtMoney(detail.settle_amount)}` : undefined} />
            <Field label="Transaction ID" value={r.trans_id} mono />
          </Section>
          <Section title="Timing">
            <Field label="Submitted" value={detail ? fmtDateTime(detail.submitted_at) : undefined} />
            <Field label="Settled" value={detail ? (detail.batch_settled_at ? fmtDateTime(detail.batch_settled_at) : '—') : undefined} />
            <Field label="Batch" value={r.batch_id ? `${r.batch_id}${detail?.batch_state ? ` · ${detail.batch_state}` : ''}` : undefined} mono />
          </Section>
          <Section title="Donor">
            <Field label="Name" value={donor} />
            <Field label="Email" value={detail?.email} />
            <Field label="Address" value={detail?.bill_to?.address} />
            <Field label="Customer profile" value={detail?.customer_profile_id} mono />
          </Section>
          <Section title="Reference">
            <Field label="Subscription" value={r.subscription_id ? `${r.subscription_id} · payment ${r.subscription_pay_num ?? '?'}` : 'one-time (no subscription)'} mono={!!r.subscription_id} />
            <Field label="Invoice / desc" value={[detail?.invoice_number, detail?.description].filter(Boolean).join(' · ')} />
            <Field label="Matched order" value={r.matched_order_number ? `${r.matched_order_number} (${matchLabelOf(r)})` : 'none'} />
          </Section>
        </Paper>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
